import { createContext, useContext, useEffect, useState, PropsWithChildren } from "react";

import { useWSTokenRequest, WSTokenResponse } from "@/queries/websocket/useWSTokenRequest";
import { LOCAL_STORAGE } from "@/constants";

interface WSTokenContextValue {
  data: WSTokenResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  setTokenDetails: (tokenDetails: WSTokenResponse) => void;
}

const WSTokenContext = createContext<WSTokenContextValue | undefined>(undefined);

export const useWSToken = () => {
  const context = useContext(WSTokenContext);
  if (!context) {
    throw new Error("useWSToken must be used within WSTokenProvider");
  }
  return context;
};

export const WSTokenProvider = ({ children }: PropsWithChildren) => {
  const { data, isLoading, error } = useWSTokenRequest();
  const [storedTokenDetails, setStoredTokenDetails] = useState<WSTokenResponse | undefined>(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE.WS_TOKEN);
      return stored ? JSON.parse(stored) : undefined;
    } catch {
      return undefined;
    }
  });

  useEffect(() => {
    if (data || storedTokenDetails) {
      try {
        localStorage.setItem(LOCAL_STORAGE.WS_TOKEN, JSON.stringify(data));
        setStoredTokenDetails(data);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [data, storedTokenDetails]);

  const displayData = data ?? storedTokenDetails;

  return (
    <WSTokenContext.Provider
      value={{
        data: displayData,
        isLoading,
        error: error as Error | null,
        setTokenDetails: setStoredTokenDetails,
      }}
    >
      {children}
    </WSTokenContext.Provider>
  );
};
